import { useState, useEffect } from 'react';
import { Share2 } from 'lucide-react';

const RATE_LIMIT_MS = 60000;
const RATE_LIMIT_KEY = 'senditmaxx_last_post_time';

const getRateLimitSeconds = () => {
  const last = parseInt(localStorage.getItem(RATE_LIMIT_KEY) || '0', 10);
  return Math.max(0, Math.ceil((RATE_LIMIT_MS - (Date.now() - last)) / 1000));
};

export const PostComposer = ({
  getUserStats,
  MIN_SENDS_TO_POST,
  shareText,
  setShareText,
  MAX_POST_LENGTH,
  isPosting,
  postError,
  shareCategory,
  setShareCategory,
  shareVibe,
  setShareVibe,
  wouldSendAgain,
  setWouldSendAgain,
  addToFeed,
}) => {
  const [rateLimitSeconds, setRateLimitSeconds] = useState(getRateLimitSeconds);

  useEffect(() => {
    setRateLimitSeconds(getRateLimitSeconds());
    const interval = setInterval(() => {
      const remaining = getRateLimitSeconds();
      setRateLimitSeconds(remaining);
      if (remaining <= 0) clearInterval(interval);
    }, 1000);
    return () => clearInterval(interval);
  }, [isPosting]);

  return (
    <div className="space-y-4 px-3 sm:px-4">
      <div className="bg-gray-900 border-3 sm:border-4 border-red-500 p-4 sm:p-6">
        {/* User Stats Display */}
        <div className="bg-black border-2 border-yellow-500 p-3 mb-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <span className="text-yellow-400 font-black text-sm uppercase">YOUR STATS</span>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-black px-2 py-1 bg-lime-500 text-black border border-lime-400">
                {getUserStats().streak} STREAK
              </span>
              <span className="text-xs font-black px-2 py-1 bg-purple-600 text-white border border-purple-400">
                {getUserStats().totalSends} SENDS
              </span>
            </div>
          </div>
          {getUserStats().totalSends < MIN_SENDS_TO_POST && (
            <p className="text-orange-400 text-xs font-bold mt-2">
              ⚠️ Complete {MIN_SENDS_TO_POST - getUserStats().totalSends} more challenge{MIN_SENDS_TO_POST - getUserStats().totalSends !== 1 ? 's' : ''} to unlock posting
            </p>
          )}
        </div>

        <textarea
          value={shareText}
          onChange={(e) => setShareText(e.target.value)}
          onFocus={(e) => {
            setTimeout(() => {
              e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 300);
          }}
          placeholder="Share your story..."
          maxLength={MAX_POST_LENGTH}
          className="w-full bg-black border-3 sm:border-4 border-red-500 p-4 sm:p-5 text-red-500 placeholder-gray-600 mb-2 min-h-24 font-bold text-base sm:text-lg focus:outline-none resize-none"
          disabled={isPosting}
        />
        <div className="text-right mb-4">
          <span className={`text-xs font-black ${shareText.length > MAX_POST_LENGTH - 20 ? 'text-orange-400' : 'text-gray-500'}`}>
            {shareText.length}/{MAX_POST_LENGTH}
          </span>
        </div>

        {postError && (
          <div className="bg-red-900 border-2 border-red-500 p-3 mb-4">
            <p className="text-red-200 font-bold text-sm">{postError}</p>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-red-500 font-black text-sm mb-2 uppercase">CATEGORY</label>
            <select
              value={shareCategory}
              onChange={(e) => setShareCategory(e.target.value)}
              className="w-full bg-black border-3 border-yellow-500 p-3 text-yellow-500 font-bold uppercase focus:outline-none"
              disabled={isPosting}
            >
              <option value="social">SOCIAL</option>
              <option value="money">MONEY</option>
              <option value="creative">CREATIVE</option>
              <option value="gym">GYM</option>
              <option value="anti-opt">ANTI-OPT</option>
              <option value="chaos">CHAOS</option>
            </select>
          </div>

          <div>
            <label className="block text-red-500 font-black text-sm mb-2 uppercase">VIBE</label>
            <select
              value={shareVibe}
              onChange={(e) => setShareVibe(e.target.value)}
              className="w-full bg-black border-3 border-lime-500 p-3 text-lime-500 font-bold uppercase focus:outline-none"
              disabled={isPosting}
            >
              <option value="lockedin">🔥 LOCKED IN</option>
              <option value="disaster">😂 HILARIOUS DISASTER</option>
              <option value="sentanyway">🤷 SENT IT ANYWAY</option>
              <option value="unhinged">💀 UNHINGED</option>
              <option value="bigW">🏆 BIG W</option>
              <option value="rejected">💔 GOT REJECTED</option>
              <option value="plot_twist">🤯 PLOT TWIST</option>
            </select>
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-red-500 font-black text-sm mb-2 uppercase">WOULD YOU SEND IT AGAIN?</label>
          <div className="flex gap-3">
            <button
              onClick={() => setWouldSendAgain(true)}
              disabled={isPosting}
              className={`flex-1 py-3 font-black uppercase border-3 transition-all ${wouldSendAgain
                ? 'bg-lime-500 text-black border-lime-400'
                : 'bg-black text-gray-500 border-gray-700 hover:border-lime-500'
                }`}
            >
              HELL YES
            </button>
            <button
              onClick={() => setWouldSendAgain(false)}
              disabled={isPosting}
              className={`flex-1 py-3 font-black uppercase border-3 transition-all ${!wouldSendAgain
                ? 'bg-orange-600 text-black border-orange-400'
                : 'bg-black text-gray-500 border-gray-700 hover:border-orange-500'
                }`}
            >
              NAH
            </button>
          </div>
        </div>

        {rateLimitSeconds > 0 ? (
          <div className="w-full bg-gray-800 border-3 border-gray-600 py-4 sm:py-5 text-center">
            <p className="text-orange-400 font-black text-lg sm:text-xl uppercase tracking-wider">
              SLOW DOWN — wait {rateLimitSeconds} seconds
            </p>
          </div>
        ) : (
          <button
            onClick={addToFeed}
            disabled={isPosting || !shareText.trim()}
            className="w-full bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-500 hover:to-orange-500 text-black py-4 sm:py-5 font-black text-lg sm:text-xl flex items-center justify-center gap-2 sm:gap-3 transition-all uppercase tracking-wider border-3 sm:border-4 border-red-400 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPosting ? (
              <>
                <div className="w-5 h-5 border-3 border-black border-t-transparent rounded-full animate-spin"></div>
                POSTING...
              </>
            ) : (
              <>
                <Share2 size={20} />
                DROP IT
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};
